import React, { useState } from "react";
import api, { formatError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { zonedWallToUtcIso, utcIsoToZonedWall } from "../lib/timezone";
import { catMeta, statusMeta } from "../lib/advisories";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import { MapPinOff } from "lucide-react";
import { toast } from "sonner";

export function StatusBadge({ status, className = "" }) {
  const m = statusMeta(status);
  const Icon = m.icon;
  return (
    <span data-testid={`status-badge-${status}`} className={`inline-flex items-center gap-1 rounded-md border px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide ${m.tint} ${className}`}>
      <Icon className="h-3 w-3" /> {m.label}
    </span>
  );
}

export function CategoryBadge({ category }) {
  const m = catMeta(category);
  const Icon = m.icon;
  return (
    <span className={`inline-flex items-center gap-1 rounded-md border px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide ${m.tint}`}>
      <Icon className="h-3 w-3" /> {category}
    </span>
  );
}

export function UnmappedBadge() {
  return (
    <span data-testid="location-not-mapped" className="inline-flex items-center gap-1 rounded-md border border-slate-200 bg-slate-50 px-1.5 py-0.5 text-[10px] font-semibold text-slate-500">
      <MapPinOff className="h-3 w-3" /> Location not mapped
    </span>
  );
}

const nextDay = (tz) => utcIsoToZonedWall(new Date(Date.now() + 24 * 3600 * 1000).toISOString(), tz);

const COPY = {
  verify: { title: "Confirm Still Active", desc: "Records that you verified this condition is still in effect and sets the next review deadline.", note: "Verification note (optional)", cta: "Confirm still active", needsReview: true, noteRequired: false },
  resolve: { title: "Mark Resolved", desc: "The advisory leaves the active map and list but stays in history. A short resolution note is required.", note: "Resolution note *", cta: "Mark resolved", needsReview: false, noteRequired: true },
  reopen: { title: "Reopen Advisory", desc: "Puts the condition back on the active map and list. Provide a reason and a new review deadline.", note: "Reason for reopening *", cta: "Reopen", needsReview: true, noteRequired: true },
  update: { title: "Add Update", desc: "Posts a timestamped update to the history. This does not count as verification.", note: "Update *", cta: "Post update", needsReview: false, noteRequired: true },
};

export function AdvisoryActionDialog({ action, advisory, onClose, onDone }) {
  const { user } = useAuth();
  const tz = user?.org_timezone;
  const [note, setNote] = useState("");
  const [review, setReview] = useState(nextDay(tz));
  const [busy, setBusy] = useState(false);
  if (!action) return null;
  const c = COPY[action];

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const v = advisory.version;
      let body;
      if (action === "verify") body = { next_review_at: zonedWallToUtcIso(review, tz), note, version: v };
      else if (action === "reopen") body = { reason: note, next_review_at: zonedWallToUtcIso(review, tz), version: v };
      else body = { note, version: v };
      const path = action === "update" ? "updates" : action;
      const { data } = await api.post(`/advisories/${advisory.id}/${path}`, body);
      toast.success(`${c.title} — saved`);
      onDone && onDone(data);
      onClose();
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail));
    } finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid={`advisory-${action}-dialog`} className="max-w-md">
        <DialogHeader>
          <DialogTitle className="font-display text-lg font-black">{c.title}</DialogTitle>
          <DialogDescription className="text-xs">{c.desc}</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          {c.needsReview && (
            <div>
              <Label htmlFor="act-review">New review date &amp; time *</Label>
              <Input id="act-review" data-testid="advisory-action-review-input" type="datetime-local" required value={review} onChange={(e) => setReview(e.target.value)} />
            </div>
          )}
          <div>
            <Label htmlFor="act-note">{c.note}</Label>
            <Textarea id="act-note" data-testid="advisory-action-note-input" rows={3} required={c.noteRequired} value={note} onChange={(e) => setNote(e.target.value)} />
          </div>
          <div className="flex justify-end gap-2">
            <Button type="button" variant="ghost" onClick={onClose}>Cancel</Button>
            <Button type="submit" data-testid="advisory-action-submit" disabled={busy} className={action === "resolve" ? "bg-slate-800 text-white hover:bg-slate-900" : "bg-blue-600 text-white hover:bg-blue-700"}>{busy ? "Saving…" : c.cta}</Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
